/**
 * Coop-admin "incoming demand" view (web) — Phase 3, Slice 8.
 *
 * Lists OPEN buyer demands the cooperative can supply against. Backed by the
 * authed GET /market/demands?status=open endpoint (admin + coop_admin; buyers
 * post demands, but buyer onboarding is deferred — admin seeds them for now)
 * via the orval-generated client (TD-103). The endpoint returns a
 * { data: [...] } envelope, so we read `.data` off `BuyerDemandListDto`. The
 * nav item is role-gated to admin / coop_admin in _app.tsx.
 *
 * Money is micro-ZARP (1e-6 ZARP) STRINGS on the wire; we convert with
 * microToZarp() and render the offered price-per-kg with the @fse/ui <Money>.
 * From a batch detail page, the coop-admin can see scored matches against these
 * demands (the Matches panel on _app.batches.$id.tsx). i18n keys live under
 * demands.* in all four web locales.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Money,
  Snackbar,
  Stack,
  StatusPill,
  Text,
  TextField,
} from '@fse/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useMarketControllerListDemands } from '../api/generated/market/market';
import type { BuyerDemandDto } from '../api/generated/models';
import { FseApiError, readCurrentRole } from '../api/client';
import { microToZarp, respondToDemand, type RespondToDemandBody } from '../api/market';

const MICRO_PER_ZAR = 1_000_000;

/** "12.50" whole-ZARP/kg → micro-ZARP integer STRING. Null when not a positive number. */
function zarPerKgToMicroString(input: string): string | null {
  const n = Number(input);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(Math.round(n * MICRO_PER_ZAR));
}

/** A positive numeric kg value (carried as a string on the wire). */
function isPositiveQty(input: string): boolean {
  const n = Number(input);
  return Number.isFinite(n) && n > 0;
}

// Roles that may respond to a demand (mirrors the server @Roles on
// POST /market/demands/:id/respond). A buyer/admin viewing /demands sees the
// list but no respond affordance.
const SELLER_ROLES = new Set(['farmer', 'group_lead', 'coop_admin']);

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatNeededBy(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return dateFormatter.format(parsed);
}

/** Format a kg range, collapsing a single-value range (min === max). */
function formatQtyRange(min: string, max: string): string {
  const minN = Number(min);
  const maxN = Number(max);
  if (Number.isFinite(minN) && Number.isFinite(maxN) && minN === maxN) {
    return `${min} kg`;
  }
  return `${min}–${max} kg`;
}

export function DemandsScreen() {
  const { t } = useTranslation();

  const demandsQuery = useMarketControllerListDemands({ status: 'open' });

  // Only sellers (farmer / group_lead / coop_admin) get the "Respond" action;
  // an admin/buyer browsing the inbox sees the list read-only. The server is the
  // authority (@Roles + 403 on a non-open demand) — this just hides a dead button.
  const canRespond = SELLER_ROLES.has(readCurrentRole() ?? '');

  // List endpoints use a { data: [...] } envelope; read `.data` off it.
  const demands = demandsQuery.data?.data ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('demands.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {demandsQuery.data
              ? t('demands.subtitleWithCount', { count: demands.length })
              : t('demands.subtitle')}
          </Text>
        </Box>
      </Box>

      {demandsQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : demandsQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => demandsQuery.refetch()}>
              {t('demands.retry')}
            </Button>
          }
        >
          {t('demands.loadError', {
            error:
              demandsQuery.error instanceof Error
                ? demandsQuery.error.message
                : String(demandsQuery.error),
          })}
        </Alert>
      ) : demands.length === 0 ? (
        <Card>
          <CardHeader title={t('demands.empty.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('demands.empty.body')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {demands.map((demand) => (
            <DemandCard key={demand.id} demand={demand} t={t} canRespond={canRespond} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface DemandCardProps {
  demand: BuyerDemandDto;
  t: TFunction;
  canRespond: boolean;
}

function DemandCard({ demand, t, canRespond }: DemandCardProps) {
  const pricePerKg = microToZarp(demand.priceZarpPerKgMicro);
  const [formOpen, setFormOpen] = useState(false);

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Text variant="title-medium" as="span">
              {demand.product}
            </Text>
            <StatusPill variant="active">{t('demands.status.open')}</StatusPill>
          </Stack>
        }
        subheader={
          <Text variant="label-medium" color="text.secondary">
            {t('demands.neededBy', { date: formatNeededBy(demand.neededBy) })}
          </Text>
        }
      />
      <CardContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Text variant="label-medium" color="text.secondary">
              {t('demands.quantity')}
            </Text>
            <Box sx={{ mt: 0.25 }}>
              <Text variant="title-medium" as="span">
                {formatQtyRange(demand.qtyKgMin, demand.qtyKgMax)}
              </Text>
            </Box>
          </Box>
          <Box>
            <Text variant="label-medium" color="text.secondary">
              {t('demands.offeredPrice')}
            </Text>
            <Box sx={{ mt: 0.25 }}>
              <Money amount={pricePerKg} unit={t('demands.perKg')} size="md" />
            </Box>
          </Box>
        </Box>

        {canRespond ? (
          formOpen ? (
            <Box sx={{ mt: 2 }}>
              <RespondForm demandId={demand.id} t={t} onDone={() => setFormOpen(false)} />
            </Box>
          ) : (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" size="small" onClick={() => setFormOpen(true)}>
                {t('demands.respond.action')}
              </Button>
            </Box>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

interface RespondFormProps {
  demandId: string;
  t: TFunction;
  onDone: () => void;
}

/**
 * Inline seller "I can supply" form. All fields optional (bare interest is fine);
 * qty/price validate only when filled. On success a toast confirms + the form
 * collapses. The server re-checks role + the demand-is-open invariant (403).
 */
function RespondForm({ demandId, t, onDone }: RespondFormProps) {
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [note, setNote] = useState('');
  const [toastOpen, setToastOpen] = useState(false);

  const qtyOk = qty.length === 0 || isPositiveQty(qty);
  const priceMicro = price.length > 0 ? zarPerKgToMicroString(price) : null;
  const priceOk = price.length === 0 || priceMicro != null;
  const formValid = qtyOk && priceOk;

  const mutation = useMutation({
    mutationFn: (body: RespondToDemandBody) => respondToDemand(demandId, body),
    onSuccess: () => {
      setToastOpen(true);
      window.setTimeout(onDone, 900);
    },
  });

  const submitError = mutation.isError
    ? mutation.error instanceof FseApiError
      ? mutation.error.message
      : String(mutation.error)
    : null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formValid) return;
    const body: RespondToDemandBody = {};
    if (qty.length > 0) body.qtyKg = String(Number(qty));
    if (priceMicro != null) body.priceZarpPerKgMicro = priceMicro;
    if (note.trim().length > 0) body.note = note.trim();
    mutation.mutate(body);
  }

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Text variant="label-medium" color="text.secondary">
          {t('demands.respond.title')}
        </Text>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
        >
          <TextField
            label={t('demands.respond.qty')}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            inputMode="decimal"
            error={!qtyOk}
            helperText={!qtyOk ? t('demands.respond.qtyError') : t('demands.respond.optional')}
            fullWidth
          />
          <TextField
            label={t('demands.respond.price')}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            error={!priceOk}
            helperText={
              !priceOk ? t('demands.respond.priceError') : t('demands.respond.optional')
            }
            fullWidth
          />
        </Box>
        <TextField
          label={t('demands.respond.note')}
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          helperText={t('demands.respond.optional')}
          fullWidth
        />

        {submitError ? <Alert severity="error">{submitError}</Alert> : null}

        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
          <Button variant="text" size="small" onClick={onDone} disabled={mutation.isPending}>
            {t('demands.respond.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={!formValid || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {mutation.isPending ? t('demands.respond.submitting') : t('demands.respond.submit')}
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          {t('demands.respond.toast')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
